<template>
  <b-modal id="mobile-filter-by-category-modal" ok-only size="lg" @shown="resetPanelPosition">
    <template #modal-header>
      <div class="modal-header-title">
        <b-button
          v-if="filterStore.filterPanels.length > 1"
          variant="outline-primary"
          @click="prevPanel"
          :disabled="activePanelIndex === 0"
        >
          <font-awesome-icon icon="chevron-left" />
        </b-button>
        {{ (activePanel && activePanel.label) || '' }}
        <b-button
          v-if="filterStore.filterPanels.length > 1"
          variant="outline-primary"
          @click="nextPanel"
          :disabled="activePanelIndex === filterStore.filterPanels.length - 1"
        >
          <font-awesome-icon icon="chevron-right" />
        </b-button>
      </div>
      <div class="modal-header-buttons">
        <div class="left">
          <div
            class="toggle-all-item"
            :class="{ disabled: activePanel.items.every((i) => i.state) }"
            button
            @click="filterStore.selectAll(activePanel.column)"
          >
            Select All
          </div>
          |
          <div
            class="toggle-all-item"
            :class="{ disabled: activePanel.items.every((i) => !i.state) }"
            button
            @click="filterStore.deselectAll(activePanel.column)"
          >
            Deselect All
          </div>
        </div>
        <div class="right">
          <div class="sort-container">
            <b-icon
              v-for="button in sortButtons"
              :key="button.sort"
              @click="filterStore.setSort(activePanel.column, button.sort)"
              class="sort-icon"
              :class="{ active: filterStore.getSort(activePanel.column) === button.sort }"
              :icon="button.icon"
            />
          </div>
        </div>
      </div>
    </template>
    <div class="filter-panel-container" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
      <div
        v-for="({ column, items }, index) in filterStore.filterPanels"
        :key="column"
        class="filter-panel"
        :style="getPanelStyle(index)"
        v-touch:swipe.left="nextPanel"
        v-touch:swipe.right="prevPanel"
      >
        <simplebar class="filter-simplebar">
          <b-list-group-item
            v-for="item in items"
            :key="`filter-bubbles-category-by-${item.value}`"
            button
            :active="item.state"
            @click="filterStore.update(column, item.value, !item.state)"
          >
            <div class="content-container">
              {{ item.value
              }}<font-awesome-icon
                class="d-none"
                :icon="item.state ? 'minus' : 'plus'"
              ></font-awesome-icon>
            </div>
          </b-list-group-item>
        </simplebar>
      </div>
    </div>
  </b-modal>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import simplebar from 'simplebar-vue'
import 'simplebar/dist/simplebar.min.css'
import { useFilterStore } from '@/store/app/filter'
import FilterByCategoryMixin from '@/mixins/FilterByCategoryMixin'

// eslint-disable-next-line no-use-before-define
@Component<MobileFilterByCategoryModal>({
  components: { simplebar },
  watch: {
    allColumns() {
      this.resetActiveColumns()
    },
  },
})
export default class MobileFilterByCategoryModal extends FilterByCategoryMixin {
  filterStore = useFilterStore()
  activePanelIndex = 0
  startX = 0
  currentX = 0

  mounted() {
    this.resetPanelPosition()
    this.resetActiveColumns()
  }

  get activePanel() {
    return this.filterStore.filterPanels[this.activePanelIndex]
  }

  prevPanel() {
    if (this.activePanelIndex > 0) {
      this.activePanelIndex--
    }
  }

  nextPanel() {
    if (this.activePanelIndex < this.filterStore.filterPanels.length - 1) {
      this.activePanelIndex++
    }
  }

  getPanelStyle(index: number) {
    const translateX = (index - this.activePanelIndex) * 100
    return {
      transform: `translateX(${translateX}%)`,
      transition: 'transform 0.3s ease',
    }
  }

  handleTouchStart(e: TouchEvent) {
    this.startX = e.touches[0].clientX
  }

  handleTouchMove(e: TouchEvent) {
    this.currentX = e.touches[0].clientX
  }

  handleTouchEnd() {
    const threshold = window.innerWidth / 4
    const distance = this.currentX - this.startX
    if (distance > threshold) {
      this.prevPanel()
    } else if (distance < -threshold) {
      this.nextPanel()
    }
    this.resetPanelPosition()
  }

  resetPanelPosition() {
    this.startX = 0
    this.currentX = 0
  }
}
</script>

<style lang="scss">
#mobile-filter-by-category-modal {
  padding: 0 !important;
  bottom: 0;
  overflow-y: hidden;
  .modal-dialog {
    width: 100%;
    height: 100%;
    padding: 1em;
    margin: 0;
    display: flex;
    flex-direction: column;
    .modal-header {
      flex-direction: column;
      width: 100%;
      .modal-header-title,
      .modal-header-buttons {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }
      .modal-header-buttons {
        margin-top: 1em;
        .left,
        .right {
          display: flex;
          align-items: center;
          gap: 0.5em;
        }
        .sort-container {
          display: flex;
          gap: 0.5em;
          .sort-icon {
            width: 1.5em;
            height: 1.5em;
            cursor: pointer;
            &.active {
              color: #007bff;
            }
          }
        }
        .toggle-all-item {
          font-weight: 600;
          font-size: 14px;
          text-transform: uppercase;
          border-bottom-width: 0;
          &.disabled {
            color: #bbb;
          }
        }
      }
    }
    .modal-content {
      height: 100%;
      display: flex;
      .modal-body {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        .filter-panel-container {
          position: relative;
          flex: 1;
          overflow-x: hidden;
          overflow-y: hidden;
          transition: transform 0.3s ease;
          touch-action: pan-y;
          &::-webkit-scrollbar {
            display: none;
          }
        }
        .filter-panel {
          position: absolute;
          border: 1px solid #ccc;
          height: 100%;
          display: flex;
          flex: 1;
          flex-direction: column;
          min-width: 100%;
        }
        .filter-simplebar {
          min-height: 0;
          flex: 1;
        }
        .list-group-item {
          border-right-width: 0;
          border-left-width: 0;
          .content-container {
            display: flex;
            width: 100%;
            justify-content: space-between;
            align-items: center;
          }
          &.active {
            background-color: #e5f2ff;
            color: black;
            border-color: #e5f2ff;
          }
          &:hover {
            svg {
              display: block !important;
              color: #bbb;
            }
          }
        }
        .list-group {
          max-height: calc(100% - 5em);
          overflow-y: scroll;
        }
      }
    }
  }
}
</style>
